import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ArrowUpDown, Loader2, FilterX, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

export default function MarginAnalysis() {
    const [loading, setLoading] = useState(false);
    const [table, setTable] = useState(null);

    // Filters
    const [clients, setClients] = useState([]);
    const [agents, setAgents] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null); // changed from 'all' string to null or value
    const [selectedAgent, setSelectedAgent] = useState('all');

    // UI States
    const [clientOpen, setClientOpen] = useState(false);

    // Data States
    const [globalMargin, setGlobalMargin] = useState(null);
    const [clientMargin, setClientMargin] = useState(null);
    const [articleBreakdown, setArticleBreakdown] = useState([]);

    // Table Config
    const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });
    const [searchTerm, setSearchTerm] = useState('');

    // 1. Load Context
    useEffect(() => {
        const ctx = localStorage.getItem('lorams_context');
        if (ctx) {
            try {
                const parsed = JSON.parse(ctx);
                if (parsed.tables && parsed.tables.length > 0) {
                    setTable(parsed.tables[0]);
                }
            } catch (e) { }
        }
    }, []);

    // 2. Initial Setup Requirements
    useEffect(() => {
        if (table) {
            fetchInitialData();
        }
    }, [table]);

    // 3. Client/Agent Change Handler
    useEffect(() => {
        if (table) {
            handleSelectionChange();
        }
    }, [selectedClient, selectedAgent, table]);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            // A. Get Filter Lists
            const filterRes = await axios.post(`${API}/reports/filters`, { table_name: table });
            if (filterRes.data.clients) setClients(filterRes.data.clients);
            if (filterRes.data.agents) setAgents(filterRes.data.agents);

            // B. Get Global Margin (Row 1)
            const globalRes = await axios.post(`${API}/reports/margin`, { table_name: table });
            if (globalRes.data.data && globalRes.data.data.length > 0) {
                setGlobalMargin(globalRes.data.data[0].value);
            }
        } catch (e) {
            console.error(e);
            toast.error('Eroare la incarcarea datelor initiale');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectionChange = async () => {
        if (!table) return;

        // Reset dependant states
        setClientMargin(null);
        setArticleBreakdown([]);

        try {
            // If "All Clients" (null) and "All Agents", stop here
            if (!selectedClient && selectedAgent === 'all') {
                return;
            }

            // 1. Get Client/Agent Margin (Row 2)
            // We want margin for the filtered selection
            const marginRes = await axios.post(`${API}/reports/margin`, {
                table_name: table,
                client: selectedClient || undefined,
                agent: selectedAgent !== 'all' ? selectedAgent : undefined
            });
            if (marginRes.data.data.length > 0) {
                setClientMargin(marginRes.data.data[0].value);
            }

            // 2. Get Article Breakdown (Row 3 Table)
            const breakdownRes = await axios.post(`${API}/reports/margin`, {
                table_name: table,
                client: selectedClient || undefined,
                agent: selectedAgent !== 'all' ? selectedAgent : undefined,
                group_by: 'article'
            });
            setArticleBreakdown(breakdownRes.data.data);

        } catch (e) {
            console.error(e);
            toast.error('Eroare la incarcarea datelor filtrate');
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedData = [...articleBreakdown]
        .filter(item =>
            String(item.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(item.description || '').toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
            if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

    if (!table) return <div className="p-8 text-gray-400">Te rog selecteaza mai intai o sursa de date.</div>;

    const formatPercent = (val) => {
        if (val === null || val === undefined) return '-';
        return `${Number(val).toFixed(2)}%`;
    };

    const formatMoney = (val) => {
        if (val === null || val === undefined) return '-';
        return Number(val).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    return (
        <div className="p-8 space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Analiza Marja & Profitabilitate
                </h1>
                <Button variant="outline" onClick={() => { setSelectedClient(null); setSelectedAgent('all'); }} className="border-white/10 text-gray-300">
                    <FilterX className="mr-2 h-4 w-4" /> Resetare
                </Button>
            </div>

            {/* FILTERS */}
            <Card className="bg-[#18181b] border-white/10">
                <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">

                    {/* CLIENT FILTER (SEARCHABLE COMBOBOX) */}
                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Client</label>
                        <Popover open={clientOpen} onOpenChange={setClientOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={clientOpen}
                                    className="w-full justify-between bg-[#27272a] border-white/20 text-white hover:bg-[#27272a] hover:text-white"
                                >
                                    {selectedClient
                                        ? clients.find((c) => c === selectedClient) || selectedClient
                                        : "Toti Clientii"}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[400px] p-0 bg-[#27272a] border-white/20 text-white">
                                <Command className="bg-[#27272a] text-white">
                                    <CommandInput placeholder="Cauta client..." className="text-white" />
                                    <CommandList>
                                        <CommandEmpty>Nu am gasit clientul.</CommandEmpty>
                                        <CommandGroup>
                                            <CommandItem
                                                value="all_clients_reset"
                                                onSelect={() => {
                                                    setSelectedClient(null);
                                                    setClientOpen(false);
                                                }}
                                                className="text-white hover:bg-white/10"
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        !selectedClient ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                Toti Clientii
                                            </CommandItem>
                                            {clients.map((client) => (
                                                <CommandItem
                                                    key={client}
                                                    value={client}
                                                    onSelect={(currentValue) => {
                                                        setSelectedClient(currentValue === selectedClient ? null : currentValue);
                                                        setClientOpen(false);
                                                    }}
                                                    className="text-white hover:bg-white/10"
                                                >
                                                    <Check
                                                        className={cn(
                                                            "mr-2 h-4 w-4",
                                                            selectedClient === client ? "opacity-100" : "opacity-0"
                                                        )}
                                                    />
                                                    {client}
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>

                    {/* AGENT FILTER */}
                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Agent</label>
                        <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                            <SelectTrigger className="bg-[#27272a] border-white/20 text-white w-full">
                                <SelectValue placeholder="Toti Agentii" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/20 text-white max-h-[400px]">
                                <SelectItem value="all">Toti Agentii</SelectItem>
                                {agents.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                </CardContent>
            </Card>

            {loading && !globalMargin ? (
                <div className="flex justify-center p-8"><Loader2 className="animate-spin text-cyan-400" /></div>
            ) : (
                <div className="space-y-6">
                    {/* ROW 1: GLOBAL CONTEXT */}
                    <Card className="bg-[#18181b] border-white/10">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-gray-400 text-transform uppercase">Marja Globala (Tot Portofoliul)</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-4xl font-bold text-white">
                                {formatPercent(globalMargin)}
                            </div>
                        </CardContent>
                    </Card>

                    {/* ROW 2: SELECTION MARGIN */}
                    {(selectedClient || selectedAgent !== 'all') && (
                        <Card className="bg-[#18181b] border-cyan-500/50">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-cyan-400 text-transform uppercase">
                                    Marja Selectie:
                                    {selectedClient && <span className="text-white ml-2 block sm:inline">{selectedClient}</span>}
                                    {selectedAgent !== 'all' && <span className="text-gray-400 ml-2 block sm:inline">(Agent: {selectedAgent})</span>}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-5xl font-bold text-white">
                                    {clientMargin !== null ? formatPercent(clientMargin) : <Loader2 className="animate-spin h-8 w-8" />}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* ROW 3: ARTICLE BREAKDOWN */}
                    {(selectedClient || selectedAgent !== 'all') && (
                        <Card className="bg-[#18181b] border-white/10">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="text-white">Detaliere Articole</CardTitle>
                                <div className="w-[300px]">
                                    <Input
                                        placeholder="Cauta articol..."
                                        className="bg-[#27272a] border-white/20 text-white"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="rounded-md border border-white/10 overflow-hidden">
                                    <Table>
                                        <TableHeader className="bg-[#27272a]">
                                            <TableRow className="border-white/10 hover:bg-transparent">
                                                <TableHead className="w-[20%] text-gray-300 cursor-pointer" onClick={() => handleSort('name')}>
                                                    Articol <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                                                </TableHead>
                                                <TableHead className="w-[30%] text-gray-300 cursor-pointer" onClick={() => handleSort('description')}>
                                                    Inventory Description <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                                                </TableHead>
                                                <TableHead className="w-[15%] text-right text-gray-300 cursor-pointer" onClick={() => handleSort('cost')}>
                                                    Cost <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                                                </TableHead>
                                                <TableHead className="w-[15%] text-right text-gray-300 cursor-pointer" onClick={() => handleSort('profit')}>
                                                    Profit <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                                                </TableHead>
                                                <TableHead className="w-[20%] text-right text-cyan-400 cursor-pointer" onClick={() => handleSort('value')}>
                                                    Marja (%) <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {sortedData.length > 0 ? (
                                                sortedData.map((item, idx) => (
                                                    <TableRow key={idx} className="border-white/10 hover:bg-white/5">
                                                        <TableCell className="font-medium text-gray-300">{item.name}</TableCell>
                                                        <TableCell className="text-gray-400 text-sm">{item.description || '-'}</TableCell>
                                                        <TableCell className="text-right text-gray-300">{formatMoney(item.cost)}</TableCell>
                                                        <TableCell className="text-right text-gray-300">{formatMoney(item.profit)}</TableCell>
                                                        <TableCell className="text-right text-cyan-400 font-bold">{formatPercent(item.value)}</TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={5} className="h-24 text-center text-gray-500">
                                                        Niciun rezultat.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
}
